import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { animated, config, useTransition } from 'react-spring';

import { CheckIcon } from '../icons';

import HeaderBox from '../header-box';
import './checklist-animated.css';

const AnimatedChecklist = ({
    animationDelayTime,
    animationTrailTime,
    header,
    intersectionThreshold,
    listItems,
    spaceAbove,
}) => {
    const intersectionElement = useRef(null);
    // const intersectionObserver = useRef(null);
    const [listVisible, setListVisible] = useState(false);

    useEffect(() => {
        let io; // IntersectionObserver
        const observedElement = intersectionElement.current;
        if (typeof window !== 'undefined' && window.IntersectionObserver) {
            const callback = entries => {
                entries.forEach(entry => {
                    // Edge doesn't currently support isIntersecting,
                    // so also test for an `intersectionThreshold` from props
                    if (entry.intersectionRatio > intersectionThreshold) {
                        // stop observing the element because it has successfully triggered
                        io.unobserve(entry.target);
                        // set state
                        setListVisible(true);
                    }
                });
            };

            // create the IntersectionObserver
            io = new window.IntersectionObserver(callback, {
                threshold: intersectionThreshold,
            });
            // set the IntersectionObserver to observe `intersectionElement`
            io.observe(intersectionElement.current);
        }

        // cleanup
        return () => {
            if (io) {
                io.unobserve(observedElement);
            }
        };
    }, [intersectionElement, intersectionThreshold]);

    const transitions = useTransition(
        listVisible ? listItems : [], // whether to show the checklist or empty array
        item => item.name, // item keys
        {
            config: config.smooth,
            delay: animationDelayTime,
            trail: animationTrailTime, // delay in ms before each enter/leave/update animation
            unique: true, // items going in and out with the same key will be re-used

            // `from` sets the base values
            from: { opacity: 0, transform: 'translate3d(0px, -100%, 0px)' },
            // when item(s) enter, styles move from `from` to `enter`
            enter: { opacity: 1, transform: 'translate3d(0px, 0%, 0px)' },
            // when item(s) leave, styles move from `enter` to `leave`
            leave: { opacity: 0, transform: 'translate3d(0px, -100%, 0px)' },
        }
    );

    return (
        <section className="checklist-animated" ref={intersectionElement}>
            <HeaderBox header={header} />
            <ul>
                {transitions.map(({ item, key, props: styles }) => (
                    <animated.li key={key} style={styles}>
                        <div className="check-icon">
                            <CheckIcon />
                        </div>
                        <div className="item-name">{item && item.name}</div>
                    </animated.li>
                ))}
            </ul>
        </section>
    );
};

AnimatedChecklist.defaultProps = {
    animationDelayTime: 250,
    animationTrailTime: 400,
    intersectionThreshold: 0.75,
    listItems: [
        { name: 'Take a small amount right off the top' },
        { name: 'Invert the spoon' },
        { name: 'Warm it up' },
        { name: "That's a ten" },
    ],
};

AnimatedChecklist.propTypes = {
    animationDelayTime: PropTypes.number,
    animationTrailTime: PropTypes.number,
    header: PropTypes.string,
    intersectionThreshold: PropTypes.number,
    listItems: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        })
    ),
};

export default AnimatedChecklist;
